import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collectionsListPath, gameDetailPath } from "../../utils/routes";
import Sidebar from "../../Components/Sidebar/Sidebar";
import styles from "./CollectionDetail.module.css";
import { get, post } from "../../utils/request";
import CommentSection from "../../Components/CommentSection/CommentSection";
import VoteSection from "../../Components/VoteSection/VoteSection";
import FollowButton from "../../Components/FollowButton/FollowButton";
import Login from "../../Components/Login/Login";
import { getFilePreviewUrl } from "../../utils/attachmentPreview";
import { t } from "../../utils/i18n";

const COLLECTION_DETAIL_API = "/game/collection/collection-info";

// 将接口返回的游戏对象映射为组件内部格式
const mapGameItem = item => {
  if (!item) return null;

  // 游戏详情入参 id：使用 collection-info 返回的 gameList.id
  const id = item.id || null;
  if (!id) return null;

  const name = item.gameName || "";
  // 封面图优先使用 gameIconId 通过 /api/file/preview 获取，这里先占位，后续再用异步补充
  const cover =
    item.gameIconUrl ||
    (Array.isArray(item.materialInfoList) &&
      item.materialInfoList.find(m => m.type === 0)?.fileUrl) ||
    "";
  const iconId = item.gameIconId || "";
  const platform = item.platform || "";
  const tag = item.categoryName || "";
  const rating = item.gameRating || "";
  const intro = item.introText || "";
  const topComment = item.topComment || item.topCommentText || "";
  const downloadNum = item.downloadNum || "";

  return {
    id,
    name,
    cover,
    iconId,
    platform,
    tag,
    rating,
    intro,
    topComment,
    downloadNum,
  };
};

const getInitials = name => {
  if (!name) return "C";
  const trimmed = name.trim();
  if (!trimmed) return "C";
  return trimmed.slice(0, 1).toUpperCase();
};

const CollectionDetail = ({
  theme,
  setTheme,
  isLoggedIn,
  handleOpenLogin,
  handleLogout,
  cartDisplayed,
  handleCloseCart,
  handleLoginSuccess,
}) => {
  const { collectionId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gamesCollapsed, setGamesCollapsed] = useState(false);
  const [following, setFollowing] = useState(false);
  const [activeHeartGameId, setActiveHeartGameId] = useState(null);

  // 移除本地评论管理状态，由 CommentSection 组件处理

  // 移除 useEffect 分步加载逻辑，由 CommentSection 内部生命周期管理

  useEffect(() => {
    const fetchDetail = async () => {
      if (!collectionId) return;

      // 路由切换到新的合集时，先清空上一条详情，避免旧封面短暂闪现
      setDetail(null);
      setGames([]);
      setFollowing(false);
      setLoading(true);

      try {
        const res = await get(COLLECTION_DETAIL_API, { collectionId });
        // 接口响应结构：{ code, data, message, success }
        const data = res?.data || {};
        // 使用合并方式更新，避免覆盖掉异步补充的 collectionIconUrl 等字段
        setDetail(prev => (prev ? { ...prev, ...data } : data));
        setFollowing(data.isTracking === true);

        // 游戏列表字段：gameList
        const rawGames = Array.isArray(data.gameList) ? data.gameList : [];
        const mappedGames = rawGames.map(mapGameItem).filter(g => g && g.id);
        setGames(mappedGames);

        // 使用每个游戏的 gameIconId 通过 /api/file/preview 异步补充封面图片
        mappedGames.forEach(game => {
          if (!game.iconId) return;
          getFilePreviewUrl(game.iconId)
            .then(url => {
              if (!url) return;
              setGames(prev =>
                prev.map(g => (g.id === game.id ? { ...g, cover: url } : g)),
              );
            })
            .catch(() => {
              // 单条失败忽略，保留占位图
            });
        });
      } catch (error) {
        console.warn("Get collection detail failed:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [collectionId]);

  // 使用详情返回的 collectionIcon 通过 /api/file/preview 获取真正封面地址
  useEffect(() => {
    const iconId = detail?.collectionIcon;
    if (!iconId) return;

    let cancelled = false;
    getFilePreviewUrl(iconId)
      .then(url => {
        if (cancelled || !url) return;
        setDetail(prev => (prev ? { ...prev, collectionIconUrl: url } : prev));
      })
      .catch(() => {
        // 出错时保留原有 cover
      });

    return () => {
      cancelled = true;
    };
  }, [detail?.collectionIcon]);

  const handleBack = () => {
    // 从合集详情返回到合集列表页
    navigate(collectionsListPath());
  };
  const handleToggleFollow = async gameId => {
    const res = await post("/user/trace/saveOrCancel", {
      objectId: String(detail?.id ?? detail?.collectionId ?? collectionId ?? ""),
      type: 2,
    });
    if (res && res.success) {
      setFollowing(prev => {
        const next = !prev;
        setDetail(current => {
          if (!current) return current;
          const prevCnt = Number(current.traceCnt ?? 0);
          const safePrevCnt = Number.isFinite(prevCnt) ? prevCnt : 0;
          const nextCnt = next
            ? safePrevCnt + 1
            : Math.max(0, safePrevCnt - 1);
          return { ...current, traceCnt: nextCnt };
        });
        if (gameId !== undefined) {
          setActiveHeartGameId(next ? gameId : null);
        } else if (!next) {
          setActiveHeartGameId(null);
        }
        return next;
      });
    }
  };

  // 移除 handleSubmitComment 和 handleToggleLike，由 CommentSection 内部处理

  // 对齐接口文档字段
  const heroTitle = detail?.collectionName || t("collection.title.default", "Collection Detail");
  const heroDescription = detail?.collectionIntro || "";
  // 封面图：只使用通过 getFilePreviewUrl 获取到的 collectionIconUrl；
  // collectionIcon 恒为 id，不再作为兜底 URL 使用。
  const cover = detail?.collectionIconUrl || "";

  // gameSize：合集中游戏数量（字符串）
  const totalGames = detail?.gameSize || games.length || 0;
  // releaseTime：合集发布时间
  const releaseTime = detail?.releaseTime || "";
  // commentCnt / evaluationCount
  const commentCnt = detail?.commentCnt ?? 0;
  const evaluationCount = detail?.evaluationCount || "";
  const followCount = detail?.traceCnt ?? 0;

  // 评论总数：优先使用详情接口返回的 commentCnt 字段
  const commentTotalFromDetail = Number(
    detail?.commentCnt != null ? detail.commentCnt : evaluationCount || 0,
  );

  // 根据每个游戏的 gameIconId（iconId）通过 /api/file/preview 补充封面图片，避免依赖列表里直接返回的 URL
  useEffect(() => {
    if (!games || games.length === 0) return;

    let cancelled = false;
    games.forEach(game => {
      if (!game.iconId) return;

      getFilePreviewUrl(game.iconId)
        .then(url => {
          if (cancelled || !url) return;
          setGames(prev =>
            prev.map(g => (g.id === game.id ? { ...g, cover: url } : g)),
          );
        })
        .catch(() => {
          // 单个失败忽略，保留原封面或占位
        });
    });

    return () => {
      cancelled = true;
    };
  }, [games.map(g => g.iconId).join(",")]);

  if (loading) {
    return (
      <div className={styles.page} data-theme={theme}>
        <div
          className={styles.container}
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <div style={{ color: "#e5e7eb", fontSize: 16 }}>
            {t("collection.loading", "Loading...")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-theme={theme}>
      {cartDisplayed ? (
        <Login
          cartDisplayed={cartDisplayed}
          handleCloseCart={handleCloseCart}
          onLoginSuccess={handleLoginSuccess}
        />
      ) : null}

      <div className={styles.container}>
        <Sidebar
          isOpen={false}
          onClose={() => {}}
          theme={theme}
          setTheme={setTheme}
          isLoggedIn={isLoggedIn}
          handleOpenLogin={handleOpenLogin}
        />

        <main className={styles.mainContent}>
          <header className={styles.headerBar}>
            <button className={styles.backBtn} onClick={handleBack}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t("collection.back", "Back to Collection")}</span>
            </button>
          </header>

          <div className={styles.scrollArea}>
            {/* Hero */}
            <section className={styles.heroSection}>
              <div className={styles.followBtnPosition}>
                <FollowButton
                  following={following}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={handleOpenLogin}
                  onClick={handleToggleFollow}
                />
              </div>
              <div className={styles.heroContent}>
                <div className={styles.heroLeft}>
                  <div className={styles.heroHeaderInfo}>
                    <div className={styles.heroCover}>
                      {cover ? (
                        <img src={cover} alt={heroTitle} />
                      ) : detail?.collectionIcon ? null : (
                        getInitials(heroTitle)
                      )}
                    </div>
                    <div className={styles.heroTitleBlock}>
                      <h1 className={styles.heroTitle}>{heroTitle}</h1>
                      <p className={styles.heroSubtitle}>
                        {t("collection.hero.subtitle", "Collection • {count} Games", {
                          count: totalGames,
                        })}
                      </p>
                    </div>
                  </div>

                  <div className={styles.heroDescription}>
                    {heroDescription?.trim() ? (
                      <div
                        className={styles.heroDescriptionRich}
                        dangerouslySetInnerHTML={{
                          __html: String(heroDescription).trim(),
                        }}
                      />
                    ) : (
                      t(
                        "collection.hero.description.default",
                        "One of the most beloved fantasy RPG series of all time, spanning multiple installments and spin-offs across various platforms.",
                      )
                    )}
                  </div>

                  <div className={styles.heroMetaRow}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>
                        {t("collection.stat.comments", "Comments")}
                      </span>
                      <span className={styles.statValue}>{commentCnt}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>
                        {t("collection.stat.followers", "Followers")}
                      </span>
                      <span className={styles.statValue}>{followCount}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>
                        {t("collection.stat.releaseTime", "Release Time")}
                      </span>
                      <span className={styles.statValue}>{releaseTime}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 管理员投票：由合集详情接口返回的 voteId 控制显隐 */}
            {detail?.voteId != null && detail.voteId !== "" && (
              <VoteSection
                voteId={detail.voteId}
                isLoggedIn={isLoggedIn}
                onRequireLogin={handleOpenLogin}
              />
            )}

            {/* Games in Collection */}
            {games.length > 0 && (
              <section className={`${styles.section}`}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    {t("collection.games.title", "{count} Games in Collection", {
                      count: games.length,
                    })}
                  </div>
                  <button
                    className={styles.iconBtn}
                    aria-label={
                      gamesCollapsed
                        ? t("collection.games.expand", "Expand games")
                        : t("collection.games.collapse", "Collapse games")
                    }
                    onClick={() => setGamesCollapsed(prev => !prev)}
                  >
                    {gamesCollapsed ? (
                      // 展开图标：向下箭头
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    ) : (
                      // 收起图标：向上箭头
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    )}
                  </button>
                </div>
                {!gamesCollapsed && (
                  <div className={styles.gamesGrid}>
                  {games.map((g, index) => (
                    <div
                      key={g.id}
                      className={styles.gameCard}
                      onClick={() => navigate(gameDetailPath(g.id))}
                    >
                      <div className={styles.gameCoverWrapper}>
                        {g.cover ? (
                          <img
                            src={g.cover}
                            alt={g.name}
                            className={styles.gameCover}
                          />
                        ) : null}

                        <div className={styles.gameCoverGradient}></div>

                        <div className={styles.gameTextOverlay}>
                          <div className={styles.gameTitle} title={g.name}>
                            {g.name}
                          </div>
                          <div className={styles.gameSubtitle}>
                            {g.tag || ""}
                          </div>
                        </div>
                      </div>

                      <div className={styles.gameBody}>
                        <div className={styles.gameMetaRow}>
                          <div className={styles.gameMetaLeft}>
                            <div className={styles.ratingBox}>
                              <span className={styles.star}>★</span>{" "}
                              {g.rating || "N/A"}
                            </div>
                            {/* 展示下载量，接口字段: downloadNum */}
                            {g.downloadNum && (
                              <div className={styles.priceBox}>
                                <span className={styles.currentPrice}>
                                  {t("collection.games.downloads", "{count} downloads", {
                                    count: g.downloadNum,
                                  })}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className={styles.gameActions}>
                            <button
                              type="button"
                              className={`${styles.actionIconBtn} ${following && activeHeartGameId === g.id ? styles.active : ""}`}
                              onClick={async e => {
                                e.stopPropagation();
                                await handleToggleFollow(g.id);
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div className={styles.releaseDate}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="3"
                              y="4"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            ></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          <span className={styles.releaseDateText}>
                            {g.intro && String(g.intro).trim() ? (
                              <span
                                className={styles.releaseDateRich}
                                dangerouslySetInnerHTML={{
                                  __html: String(g.intro).trim(),
                                }}
                              />
                            ) : (
                              t(
                                "collection.games.intro.default",
                                "come and join us",
                              )
                            )}
                          </span>
                        </div>

                        {g.topComment && String(g.topComment).trim() ? (
                          <div className={styles.hotCommentRank}>
                            <span className={styles.rankIcon}>
                              {t("home.rank.top", "TOP")}
                            </span>
                            <span
                              className={styles.hotCommentRich}
                              dangerouslySetInnerHTML={{
                                __html: String(g.topComment).trim(),
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </section>
            )}

            {/* Community Discussion - 使用通用的 CommentSection 组件 */}
            <section className={styles.communitySection}>
              <CommentSection
                category="COLLECTION"
                objectId={collectionId}
                isLoggedIn={isLoggedIn}
                onRequireLogin={handleOpenLogin}
                hideTabs={false}
                totalFromDetail={commentTotalFromDetail}
                onCommentTotalChange={nextTotal =>
                  setDetail(prev =>
                    prev ? { ...prev, commentCnt: nextTotal } : prev,
                  )
                }
              />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CollectionDetail;
